import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollToSection } from '../SectionsDrawer/useScrollToSection';
import { useAddBlankSection } from './useAddBlankSection';
import { ImportSectionsDialog } from 'components/library/sections/ImportSectionsDialog';
import { ImportPdfDialog } from './ImportPdfDialog';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'utils/queryKeys';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { sectionToolbarAddMenuTestIds } from './SectionToolbarAddMenu.test-ids';

interface SectionToolbarAddMenuProps {
  sectionId: string;
}

export function SectionToolbarAddMenu({ sectionId }: SectionToolbarAddMenuProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLibrarySectionsDialogOpen, setIsLibrarySectionsDialogOpen] = useState(false);
  const [isImportPdfDialogOpen, setIsImportPdfDialogOpen] = useState(false);
  const scrollToSection = useScrollToSection();
  const addBlankSection = useAddBlankSection({ sectionId });
  const { [FeatureFlags.pdfImport]: isPdfImportEnabled } = useFeatureFlag([FeatureFlags.pdfImport]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLibrarySectionsDialog = () => {
    setIsLibrarySectionsDialogOpen((isPreviouslyOpen) => {
      if (isPreviouslyOpen === false) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.foldersTree('SECTION') });
      }

      return !isPreviouslyOpen;
    });
  };

  return (
    <>
      <IconButton ref={menuAnchorRef} onClick={toggleMenu}>
        <AddIcon data-testid={sectionToolbarAddMenuTestIds.addIcon} fill={'none'} stroke={'none'} color={'primary'} />
      </IconButton>

      <Menu
        anchorEl={menuAnchorRef.current}
        open={isMenuOpen}
        onClose={toggleMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
          borderRadius: (theme) => theme.shape.borderRadius,
        }}
      >
        <MenuItem
          key="add-blank"
          dense
          onClick={() => {
            addBlankSection();
            setIsMenuOpen(false);
          }}
        >
          <Typography variant="body1" color="primary">
            {t('editor.sections.add_blank_section')}
          </Typography>
        </MenuItem>

        <MenuItem
          key="add-from-library"
          dense
          onClick={() => {
            toggleLibrarySectionsDialog();
            setIsMenuOpen(false);
          }}
        >
          <Typography variant="body1" color="primary">
            {t('editor.sections.add_from_library')}
          </Typography>
        </MenuItem>

        {isPdfImportEnabled && (
          <MenuItem
            key="add-pdf-upload"
            dense
            onClick={() => {
              setIsImportPdfDialogOpen(true);
              setIsMenuOpen(false);
            }}
          >
            <Typography variant="body1" color="primary">
              {t('editor.sections.add_pdf_upload')}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      <ImportSectionsDialog
        afterSectionId={sectionId}
        isOpen={isLibrarySectionsDialogOpen}
        onClose={toggleLibrarySectionsDialog}
        onImported={(ids) => scrollToSection(ids[0])}
      />

      <ImportPdfDialog
        afterSectionId={sectionId}
        isOpen={isImportPdfDialogOpen}
        onClose={() => setIsImportPdfDialogOpen(false)}
        onImported={(ids) => scrollToSection(ids[0])}
      />
    </>
  );
}
